<template>
  <div class="organization-payment-methods-outbound">
    <div class="d-flex align-center mb-4">
      <v-spacer></v-spacer>
      <!-- Admin sync button - discreet -->
      <v-btn v-if="currentUser && currentUser.isZE" x-small text @click="reload" :loading="loading" class="text--secondary">
        <v-icon small left>mdi-sync</v-icon>
        {{ $t('payout.resynchronize') }}
      </v-btn>
      <audit-trail-button class="ml-2" entity-type="ConnectedBeneficiary,ExternalBankAccount" :organization-id="$route.params.id" />
    </div>

    <v-alert v-if="error" type="error" outlined dense class="mb-4">{{ error }}</v-alert>
    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4"/>

    <!-- Beneficiary section with QR code on the right -->
    <v-card outlined class="mb-6">
      <v-card-title>
        <v-icon left>mdi-wallet</v-icon>
        {{ $t('payout.configuration') }}
      </v-card-title>
      <v-card-text>
        <v-row v-if="beneficiary">
          <!-- Left side: Configuration info and actions -->
          <v-col cols="12" md="8">
            <div>
          <!-- Status badge with better labels -->
          <div class="mb-4">
            <v-chip
              :color="beneficiaryStatusColor"
              dark
              class="mr-2"
            >
              <v-icon left small>{{ beneficiaryStatusIcon }}</v-icon>
              {{ beneficiaryStatusLabel }}
            </v-chip>
          </div>

          <!-- Action button based on status -->
          <div v-if="beneficiary.onboardingStatus !== 'active' && beneficiary.onboardingStatus !== 'complete'">
            <v-alert type="info" outlined dense class="mb-4">
              <div class="mb-2">{{ $t('payout.complete-config-to-receive') }}</div>
              <div class="text-caption">
                <strong>{{ $t('payout.how-it-works') }}</strong>
                <ul class="mt-1 ml-4">
                  <li>{{ $t('payout.click-configure-below') }}</li>
                  <li>{{ $t('payout.enter-bank-info') }}</li>
                  <li>{{ $t('payout.once-validated') }}</li>
                </ul>
              </div>
            </v-alert>
            <v-btn color="primary" @click="openEmbeddedOnboarding" :loading="actionLoading">
              <v-icon left>mdi-cog</v-icon>
              {{ $t('payout.configure') }}
            </v-btn>
          </div>
          <div v-else>
            <v-alert type="success" outlined dense class="mb-4">
              <div>{{ $t('payout.payouts-activated') }}</div>
            </v-alert>
            <v-btn text color="blue" @click="openTransactions" :loading="actionLoading" class="mr-2">
              <v-icon left small>mdi-history</v-icon>
              {{ $t('payment-method-ui.view-transactions') }}
            </v-btn>
            <v-btn text color="primary" @click="openEmbeddedOnboarding" :loading="actionLoading" class="mr-2">
              <v-icon left small>mdi-pencil</v-icon>
              {{ $t('payout.update-my-information') }}
            </v-btn>
            <v-btn outlined color="primary" @click="openOnboardingLink" :loading="actionLoading">
              <v-icon left small>mdi-open-in-new</v-icon>
              {{ $t('payout.open-onboarding') }}
            </v-btn>
          </div>

          <!-- Debug info for admins only -->
          <div v-if="currentUser && currentUser.isZE" class="mt-4 pa-2" style="background: #f5f5f5; border-radius: 4px;">
            <div class="text-caption text--secondary mb-2">
              <div><strong>{{ $t('payout.admin-debug') }}</strong></div>
              <div>{{ $t('payout.beneficiary-id') }} {{ beneficiary.beneficiaryId }}</div>
              <div>{{ $t('payout.owner-label') }} {{ beneficiary.ownerType }} • {{ beneficiary.ownerId }}</div>
              <div v-if="beneficiary.stripeConnectedAccountId">{{ $t('payout.account-label') }} {{ beneficiary.stripeConnectedAccountId }}</div>
            </div>
            <v-btn x-small outlined color="primary" @click="syncBeneficiary" :loading="actionLoading">
              <v-icon small left>mdi-sync</v-icon>
              {{ $t('payout.sync-from-stripe') }}
            </v-btn>
          </div>
            </div>
          </v-col>

          <!-- Right side: QR Code -->
          <v-col cols="12" md="4">
            <div class="text-center">
              <div class="text-subtitle-2 mb-3">{{ $t('qr.onboarding-link-qr') }}</div>
              <qr-link-actions
                :link="onboardingRelayLink"
                :size="200"
                :show-refresh="false"
                :show-link="false"
                :filename="`payout-onboarding-${organizationId}-qr.png`"
                compact
              />
            </div>
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col cols="12">
          <v-alert type="info" outlined dense class="mb-4">
            <div class="mb-2">{{ $t('payout.not-configured-yet') }}</div>
            <div class="text-caption">
              <strong>{{ $t('payout.why-configure') }}</strong>
              <ul class="mt-1 ml-4">
                <li>{{ $t('payout.receive-payments-automatically') }}</li>
                <li>{{ $t('payout.track-transactions-real-time') }}</li>
                <li>{{ $t('payout.manage-bank-accounts') }}</li>
              </ul>
            </div>
          </v-alert>
          <v-btn color="primary" @click="createOrFetchBeneficiary" :loading="actionLoading">
            <v-icon left>mdi-cog</v-icon>
            {{ $t('payout.configure') }}
          </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Bank accounts section - Read only -->
    <v-card outlined class="mb-6">
      <v-card-title>
        <v-icon left>mdi-bank</v-icon>
        {{ $t('payout.my-bank-accounts') }}
      </v-card-title>
      <v-card-text>
        <div v-if="!beneficiary">
          <v-alert type="info" outlined dense>
            <div>{{ $t('payout.configure-payouts-first') }}</div>
          </v-alert>
        </div>
        <div v-else>
          <div v-if="banks.items && banks.items.length">
            <v-alert type="info" outlined dense class="mb-4">
              <v-icon left small>mdi-information</v-icon>
              {{ $t('payout.banks-configured-for-payouts') }}
            </v-alert>

            <v-simple-table>
              <thead>
              <tr>
                <th class="text-left">{{ $t('payout.status-header') }}</th>
                <th class="text-left">{{ $t('payout.holder-header') }}</th>
                <th class="text-left">{{ $t('payout.iban-header') }}</th>
                <th class="text-left">{{ $t('payout.bank-header') }}</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="b in banks.items" :key="b.externalBankId">
                <td>
                  <v-chip x-small :color="b.status==='verified' ? 'green' : 'grey'" dark>
                    {{ b.status === 'verified' ? $t('payout.verified') : b.status }}
                  </v-chip>
                </td>
                <td>{{ b.holderName }}</td>
                <td>{{ b.country }} {{ b.currency }} •••• {{ b.ibanLast4 }}</td>
                <td>{{ b.bankName }}</td>
              </tr>
              </tbody>
            </v-simple-table>
          </div>
          <div v-else>
            <v-alert type="warning" outlined dense>
              <div class="mb-2">{{ $t('payout.no-bank-account-configured') }}</div>
              <div class="text-caption">{{ $t('payout.to-add-bank-account') }}</div>
            </v-alert>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Create payout section -->
    <v-card outlined>
      <v-card-title>
        <v-icon left>mdi-cash-fast</v-icon>
        {{ $t('payout.send-money') }}
      </v-card-title>
      <v-card-text>
        <div v-if="!beneficiary">
          <v-alert type="warning" outlined dense>
            <div>{{ $t('payout.must-configure-first') }}</div>
          </v-alert>
        </div>
        <div v-else-if="beneficiary.onboardingStatus !== 'active' && beneficiary.onboardingStatus !== 'complete'">
          <v-alert type="warning" outlined dense>
            <div>{{ $t('payout.must-complete-config') }}</div>
          </v-alert>
        </div>
        <div v-else-if="!banks.items || banks.items.length === 0">
          <v-alert type="warning" outlined dense>
            <div>{{ $t('payout.must-add-bank-account') }}</div>
          </v-alert>
        </div>
        <div v-else>
          <v-form ref="payoutFormRef" v-model="payoutFormValid" lazy-validation>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="payoutForm.amountEuro"
                  :label="$t('payout.amount-euro')"
                  outlined
                  dense
                  type="number"
                  step="0.01"
                  min="0.01"
                  suffix="€"
                  :rules="[v => !!v && parseFloat(v) > 0 || $t('required')]"
                  @input="updateAmountCents"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="payoutForm.currency"
                  :items="['EUR', 'USD', 'GBP']"
                  :label="$t('currency')"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="payoutForm.externalBankAccountId"
                  :items="bankAccountOptions"
                  item-text="label"
                  item-value="value"
                  :label="$t('payout.select-bank-account')"
                  outlined
                  dense
                  :rules="[v => !!v || $t('required')]"
                >
                  <template v-slot:selection="{ item }">
                    <v-chip x-small :color="item.color" dark class="mr-2">{{ item.status }}</v-chip>
                    <span>{{ item.label }}</span>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-chip x-small :color="item.color" dark class="mr-2">{{ item.status }}</v-chip>
                    <span>{{ item.label }}</span>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="payoutForm.reference"
                  :label="$t('payout.reference')"
                  outlined
                  dense
                  :hint="$t('payout.reference-hint')"
                  persistent-hint
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="payoutForm.note"
                  :label="$t('payout.note')"
                  outlined
                  dense
                  rows="2"
                  :hint="$t('payout.note-hint')"
                  persistent-hint
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>

          <v-alert v-if="payoutError" type="error" outlined dense class="mt-4">
            <div><strong>{{ $t('error') }}</strong></div>
            <div class="text-caption">{{ payoutError }}</div>
          </v-alert>

          <v-alert v-if="payoutSuccess" type="success" outlined dense class="mt-4">
            <div><strong>{{ $t('payout.payout-created-success') }}</strong></div>
            <div class="text-caption">{{ $t('payout.amount-label') }} {{ (payoutSuccess.amount / 100).toFixed(2) }} {{ payoutSuccess.currency }}</div>
            <div class="text-caption">{{ $t('payout.status-label') }} {{ payoutSuccess.status === 'pending' ? $t('payout.status-in-progress') : payoutSuccess.status }}</div>
            <!-- Admin debug info only -->
            <div v-if="currentUser && currentUser.isZE" class="text-caption text--secondary mt-2">
              {{ $t('payout.transfer-id') }}: {{ payoutSuccess.stripeTransferId }}
            </div>
          </v-alert>
        </div>
      </v-card-text>
      <v-card-actions v-if="beneficiary && (beneficiary.onboardingStatus === 'active' || beneficiary.onboardingStatus === 'complete') && banks.items && banks.items.length > 0">
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          :disabled="!payoutFormValid || !payoutForm.amountEuro || parseFloat(payoutForm.amountEuro) <= 0"
          :loading="actionLoading"
          @click="executePayout"
          small
        >
          <v-icon left small>mdi-send</v-icon>{{ $t('payout.send-payout') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Embedded Onboarding Dialog -->
    <v-dialog v-model="showEmbeddedOnboarding" max-width="900px" persistent>
      <v-card>
        <v-card-title>
          <v-icon left>mdi-wallet-outline</v-icon>
          <span>{{ $t('payout.bank-info-configuration') }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeEmbeddedOnboarding">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <div id="embedded-onboarding-container" style="min-height: 500px;"></div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Beneficiary Transactions Dialog -->
    <payment-transaction-list-modal
      v-model="transactionsDialog"
      :beneficiary-id="transactionsBeneficiary?.beneficiaryId"
    />
  </div>
</template>

<script>
import AuditTrailButton from "@elements/AuditTrailButton.vue";
import PayoutRepository from '@repository/PayoutRepository'
import StripeService from '@service/StripeService'
import QrLinkActions from '@/components/shared/QrLinkActions.vue'
import QrCodeService from '@service/QrCodeService'
import PaymentTransactionListModal from '@elements/PaymentTransactionListModal.vue'

export default {
  components: {
    AuditTrailButton,
    QrLinkActions,
    PaymentTransactionListModal
  },

  data() {
    return {
      loading: false,
      actionLoading: false,
      error: null,
      payoutError: null,
      beneficiary: null,
      banks: { items: [] },
      payoutFormValid: false,
      payoutForm: { amountEuro: '', amountCents: 0, currency: 'EUR', reference: '', note: '', externalBankAccountId: null },
      payoutSuccess: null,
      showEmbeddedOnboarding: false,
      stripeConnectInstance: null,
      transactionsDialog: false,
      transactionsBeneficiary: null,
    }
  },

  computed: {
    organizationId() {
      return this.$route.params.id
    },
    organization() {
      return this.$parent.organization
    },

    onboardingRelayLink() {
      if (!this.organizationId) return null
      const route = this.$router.resolve({
        name: 'organizations.payment-onboarding',
        params: { id: this.organizationId }
      })
      return window.location.origin + route.href
    },

    bankAccountOptions() {
      if (!this.banks?.items || this.banks.items.length === 0) return []
      return this.banks.items.map(bank => ({
        value: bank.externalBankId || bank.id,
        label: `${bank.holderName} - ${bank.country} ${bank.currency} •••• ${bank.ibanLast4}`,
        status: bank.status,
        color: bank.status === 'verified' ? 'green' : 'grey'
      }))
    },

    beneficiaryStatusLabel() {
      if (!this.beneficiary) return this.$t('payout.status-not-configured')
      const status = this.beneficiary.onboardingStatus
      const statusMap = {
        'active': this.$t('payout.status-activated'),
        'complete': this.$t('payout.status-activated'),
        'pending': this.$t('payout.status-configuration-in-progress'),
        'restricted': this.$t('payout.status-suspended'),
        'restricted_soon': this.$t('payout.status-action-required'),
        'requirements_due': this.$t('payout.status-action-required')
      }
      return statusMap[status] || status || this.$t('payout.status-not-configured')
    },

    beneficiaryStatusColor() {
      if (!this.beneficiary) return 'grey'
      const status = this.beneficiary.onboardingStatus
      const colorMap = {
        'active': 'green',
        'complete': 'green',
        'pending': 'orange',
        'restricted': 'red',
        'restricted_soon': 'orange',
        'requirements_due': 'orange'
      }
      return colorMap[status] || 'grey'
    },

    beneficiaryStatusIcon() {
      if (!this.beneficiary) return 'mdi-close-circle'
      const status = this.beneficiary.onboardingStatus
      const iconMap = {
        'active': 'mdi-check-circle',
        'complete': 'mdi-check-circle',
        'pending': 'mdi-clock-outline',
        'restricted': 'mdi-alert-circle',
        'restricted_soon': 'mdi-alert',
        'requirements_due': 'mdi-alert'
      }
      return iconMap[status] || 'mdi-help-circle'
    }
  },

  mounted() {
    this.loadInitialData()

    // Détecter si on revient de l'onboarding Stripe hosted
    if (this.$route.query.account) {
      // Message de succès (optionnel)
      this.$dialog?.notify?.success && this.$dialog.notify.success(
        this.$t('payout.onboarding-completed') || 'Configuration terminée avec succès'
      )
      // Nettoyer l'URL (retirer le paramètre account)
      this.$router.replace({ query: {} }).catch(() => {})
    }
  },

  methods: {
    loadInitialData() {
      this.loading = true
      this.error = null
      PayoutRepository.createBeneficiary({ ownerType: 'organization', ownerId: this.organizationId })
        .then(result => {
          this.beneficiary = result?.beneficiary || result
          if (this.beneficiary?.beneficiaryId) {
            this.reload()
          }
        })
        .catch(e => {
          this.error = e?.response?.data?.message || e?.message || String(e)
        })
        .finally(() => {
          this.loading = false
        })
    },

    reload() {
      this.error = null
      if (!this.beneficiary) return
      this.loading = true
      PayoutRepository.getBeneficiary(this.beneficiary.beneficiaryId)
        .then(b => {
          this.beneficiary = b?.beneficiary || b || this.beneficiary
          return PayoutRepository.listExternalBankAccounts(this.beneficiary.beneficiaryId)
        })
        .then(banks => {
          this.banks = banks?.banks || banks || { items: [] }
          // Auto-select first verified bank account
          if (this.banks.items && this.banks.items.length > 0 && !this.payoutForm.externalBankAccountId) {
            const verifiedBank = this.banks.items.find(b => b.status === 'verified')
            const defaultBank = verifiedBank || this.banks.items[0]
            this.payoutForm.externalBankAccountId = defaultBank.externalBankId || defaultBank.id
          }
        })
        .catch(e => {
          this.error = e?.response?.data?.message || e?.message || String(e)
        })
        .finally(() => {
          this.loading = false
        })
    },

    createOrFetchBeneficiary() {
      this.actionLoading = true
      this.error = null
      PayoutRepository.createBeneficiary({ ownerType: 'organization', ownerId: this.organizationId })
        .then(result => {
          this.beneficiary = result?.beneficiary || result
          this.reload()
          // Auto-open embedded onboarding after creation
          if (this.beneficiary && (this.beneficiary.onboardingStatus !== 'active' && this.beneficiary.onboardingStatus !== 'complete')) {
            this.openEmbeddedOnboarding()
          }
        })
        .catch(e => {
          this.error = e?.response?.data?.message || e?.message || String(e)
        })
        .finally(() => {
          this.actionLoading = false
        })
    },

    openEmbeddedOnboarding() {
      if (!this.beneficiary?.beneficiaryId) return
      this.actionLoading = true
      this.error = null

      // Décider entre hosted (redirect) et embedded selon si c'est la première fois
      const isFirstOnboarding = !this.beneficiary.detailsSubmitted &&
        (this.beneficiary.onboardingStatus !== 'active' && this.beneficiary.onboardingStatus !== 'complete')

      if (isFirstOnboarding) {
        // Première fois → Hosted onboarding (redirect vers Stripe)
        this.useHostedOnboarding()
      } else {
        // Mise à jour → Embedded onboarding (dans notre dialog)
        this.initializeEmbeddedOnboarding()
          .catch(e => {
            this.error = e?.response?.data?.message || e?.message || String(e)
          })
          .finally(() => {
            this.actionLoading = false
          })
      }
    },

    useHostedOnboarding() {
      // Construire l'URL de retour (URL actuelle de la page)
      const returnUrl = window.location.origin + this.$route.fullPath

      // Utiliser le mode hosted (redirection) - pas d'embedded
      PayoutRepository.createOnboardingLink(this.beneficiary.beneficiaryId, false, returnUrl)
        .then(res => {
          const url = res?.url
          if (!url) {
            throw new Error('No onboarding URL returned from API')
          }
          // Rediriger vers Stripe
          window.location.href = url
        })
        .catch(e => {
          this.error = e?.response?.data?.message || e?.message || String(e)
          this.actionLoading = false
        })
    },

    initializeEmbeddedOnboarding() {
      const containerId = 'embedded-onboarding-container'

      return PayoutRepository.createOnboardingLink(this.beneficiary.beneficiaryId, true)
        .then(res => {
          const clientSecret = res?.clientSecret
          if (!clientSecret) {
            throw new Error('No client secret returned from API')
          }

          // Show the dialog for embedded mode
          this.showEmbeddedOnboarding = true

          return clientSecret
        })
        .then(clientSecret => {
          // Initialize Stripe Connect via StripeService
          return StripeService.initStripeConnect(clientSecret)
        })
        .then(stripeConnectInstance => {
          this.stripeConnectInstance = stripeConnectInstance
          // Create the account onboarding component
          const accountOnboarding = stripeConnectInstance.create('account-onboarding')

          // Configure collection options
          accountOnboarding.setCollectionOptions({
            fields: 'currently_due',
            futureRequirements: 'include'
          })

          // Set the onExit callback
          accountOnboarding.setOnExit(() => {
            this.closeEmbeddedOnboarding()
          })

          // Mount the component
          const container = document.getElementById(containerId)
          if (container) {
            container.innerHTML = ''
            container.appendChild(accountOnboarding)
          } else {
            throw new Error(`Container element '${containerId}' not found`)
          }
        })
        .catch(e => {
          this.error = e?.message || String(e)
        })
    },

    closeEmbeddedOnboarding() {
      this.showEmbeddedOnboarding = false
      // Refresh beneficiary data
      this.reload()
      // Clean up the Stripe instance and containers
      if (this.stripeConnectInstance) {
        this.stripeConnectInstance = null
      }
      // Clean up container
      const container = document.getElementById('embedded-onboarding-container')
      if (container) container.innerHTML = ''
    },

    syncBeneficiary() {
      if (!this.beneficiary?.beneficiaryId) return
      this.actionLoading = true
      this.error = null
      PayoutRepository.syncBeneficiary(this.beneficiary.beneficiaryId)
        .then(() => {
          // Reload data after sync
          this.reload()
          this.$dialog?.notify?.success && this.$dialog.notify.success(this.$t('payout.sync-success') || 'Synchronisation réussie')
        })
        .catch(e => {
          this.error = e?.response?.data?.message || e?.message || String(e)
        })
        .finally(() => {
          this.actionLoading = false
        })
    },

    openOnboardingLink() {
      if (this.onboardingRelayLink) {
        window.open(this.onboardingRelayLink, '_blank')
      }
    },
    updateAmountCents() {
      const euro = parseFloat(this.payoutForm.amountEuro)
      if (!isNaN(euro) && euro > 0) {
        this.payoutForm.amountCents = Math.round(euro * 100)
      } else {
        this.payoutForm.amountCents = 0
      }
    },
    executePayout() {
      if (!this.beneficiary?.beneficiaryId) return
      if (!this.payoutForm.amountCents || this.payoutForm.amountCents <= 0) return

      this.$dialog?.confirm?.({
        text: this.$t('payout.confirm-send', {
          amount: (this.payoutForm.amountCents / 100).toFixed(2),
          currency: this.payoutForm.currency
        })
      }).then(ok => {
        if (!ok) return
        this.actionLoading = true
        this.payoutError = null
        this.payoutSuccess = null

        PayoutRepository.createPayout(this.beneficiary.beneficiaryId, {
          amount: this.payoutForm.amountCents,
          currency: this.payoutForm.currency,
          reference: this.payoutForm.reference || undefined,
          note: this.payoutForm.note || undefined,
          externalBankAccountId: this.payoutForm.externalBankAccountId
        })
          .then(result => {
            this.payoutSuccess = result
            this.payoutForm.amountEuro = ''
            this.payoutForm.amountCents = 0
            this.payoutForm.reference = ''
            this.payoutForm.note = ''
            // Keep the selected bank account for next payout
            this.$dialog?.notify?.success && this.$dialog.notify.success(this.$t('payout.success'))
          })
          .catch(e => {
            const errorMsg = e?.response?.data?.message || e?.message || String(e)
            this.payoutError = errorMsg
            // Also show a toast notification so user sees it immediately
            this.notifyError(errorMsg)
          })
          .finally(() => {
            this.actionLoading = false
          })
      })
    },

    openTransactions() {
      if (!this.beneficiary) return
      this.transactionsBeneficiary = this.beneficiary
      this.transactionsDialog = true
    }
  }
}
</script>

<style lang="scss">
.organization-payment-methods-outbound {}
</style>
